import React, { useMemo, useState } from 'react';
import { ICONS, PATHS } from 'src/common/constants';
import Button from '../Button';
import Empty from '../Empty';
import Icon from '../Icon';
import Overlay from '../Overlay';
import Table from 'src/components/Table';
import { useSelector } from 'react-redux';
import { selectSearch } from 'src/store/selectors/search';
import Loader from '../Loader';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { variables } from 'src/Tenants';
import { IContactFilters } from 'src/pages/Contacts/CustomHooks/useContactFilters';
import { IContactTableRow, transformContactData } from './data';
import { createContactsTableColumns } from 'src/components/ContactsTable/helpers';
import ContactsTableActions from 'src/components/ContactsTable/ContactsTableActions';
import ContactPropertyDetailsDialogue from 'src/components/ContactPropertyDialogue/ContactPropertyDialogue';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { ITableColumn } from 'src/components/Table/types';
import { useContactsSuiteActionsDataFromContext } from 'src/pages/Contacts/ContactsContext/ContactsContext';
import {
  ContactFormActionsContext,
  useContactFormActionsFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import ContactForm from 'src/components/ContactForm/ContactForm';
import {
  useContactFormSubmissionContacts,
} from 'src/pages/SharedPageCustomHooks/UseContactSaveContactsPage/useContactFormSubmission';
import { enrichContactDataForEdit } from 'src/utils/CommonHelpers/commonhelpers';

interface ContactsTableProps {
  contacts: IContact[];
  onDeleteContact: (data: any) => void;
  sendResetPasswordLinkToUser: (data: any) => void;
  sendActivationLinkToUser: (data: any) => void;
  onSelectContact: (userId: any) => void;
  selectedContacts: any[];
  onSendInvitation: () => void;
  onShowFilterModal?: () => void;
  activeFilters: IContactFilters;
  resetFilters: any;
  removeSingleActiveFilter: any;
  hasActiveFilters: boolean;
  refetchContactFormData: any;
  allSelected: any;
  onSelectAllContacts: any;
  isLoadingContacts: boolean;
}

const ContactsTable = ({
  contacts = [],
  onDeleteContact,
  sendResetPasswordLinkToUser,
  sendActivationLinkToUser,
  onSelectContact,
  selectedContacts = [],
  onSendInvitation,
  onShowFilterModal,
  activeFilters,
  resetFilters,
  removeSingleActiveFilter,
  hasActiveFilters,
  refetchContactFormData,
  allSelected,
  onSelectAllContacts,
  isLoadingContacts,
}: ContactsTableProps) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [selectedContactPropertyDetails, setSelectedContactPropertyDetails] =
    useState<IContact | null>(null);

  const {
    clearContactFormData,
    initializeContactFormDataFromEdit,
  }: ContactFormActionsContext = useContactFormActionsFromContext();
  
  const handleCloseForm = () => {
    setShowOverlay(false);
    clearContactFormData();
  };
  
  const { handleSubmitContactForm } = useContactFormSubmissionContacts({
    onSuccess: () => refetchContactFormData(),
    onClose: handleCloseForm,
  });

  const { isLoading: isLoadingSuiteActions } =
    useContactsSuiteActionsDataFromContext();

  const updateSelectedContactPropertyDetails = (contactToSelect: IContact) => {
    setSelectedContactPropertyDetails(contactToSelect);
  };

  const search = useSelector(selectSearch);
  const loader = useSelector(selectFormLoader);

  const handleAddContact = () => {
    setShowOverlay(true);
  };

  const handleEditContact = (contactData: any) => {
    setShowOverlay(true);
    const enrichedContactData: any = enrichContactDataForEdit(contactData);
    initializeContactFormDataFromEdit(enrichedContactData);
  };

  const tableColumns: ITableColumn[] = useMemo(
    () =>
      createContactsTableColumns({
        selectedContacts,
        onSelectContact,
        handleEditContact,
        onDeleteContact,
        variables,
        PATHS,
        ICONS,
        updateSelectedContactPropertyDetails,
        allSelected,
        onSelectAllContacts,
      }),
    [selectedContacts, onSelectContact, onDeleteContact, handleEditContact]
  );

  const transformedData: IContactTableRow[] = useMemo(() => {
    if (!contacts || contacts.length === 0) {
      return [];
    }
    return transformContactData(contacts);
  }, [contacts]);

  // Safely check if contacts exist and have length.
  const hasContacts: boolean = Array.isArray(contacts) && contacts.length > 0;

  return (
    <>
      {loader || isLoadingContacts || isLoadingSuiteActions ? (
        <Loader center={true} />
      ) : (
        <ContactsTableActions
          onAddContact={handleAddContact}
          onShowFilterModal={onShowFilterModal}
          hasActiveFilters={hasActiveFilters}
          resetFilters={resetFilters}
          selectedContacts={selectedContacts}
          onSendInvitation={onSendInvitation}
          activeFilters={activeFilters}
          removeSingleActiveFilter={removeSingleActiveFilter}
        />
      )}
      {hasContacts ? (
        <div style={{ overflowX: 'scroll', width: '100%' }}>
          <Table applyMinWidth columns={tableColumns} data={transformedData} />
        </div>
      ) : (
        <Empty
          direction="ttb"
          headline={hasActiveFilters ? 'No results found' : 'No contacts yet'}
          strapline={
            hasActiveFilters
              ? 'Please update your filters and try again.'
              : "Keep all your contacts here so it's easier for you to manage."
          }
          searchIsActive={search && search.contacts ? search.contacts : ''}
          cta={
            <>
              {hasActiveFilters ? (
                <Button
                  color="teal"
                  label="Clear Filters"
                  onClick={resetFilters}
                  icon={<Icon glyph="filter" weight="light" size="small" />}
                  size="small"
                />
              ) : (
                <Button
                  color="teal"
                  label="Add Contact"
                  onClick={handleAddContact}
                  icon={<Icon glyph="user" weight="light" size="small" />}
                  size="small"
                />
              )}
            </>
          }
          image={
            <img alt="No contacts found" src="/assets/empty/contacts.svg" />
          }
        />
      )}
      <ContactPropertyDetailsDialogue
        isOpen={!!selectedContactPropertyDetails}
        onClose={() => setSelectedContactPropertyDetails(null)}
        contactData={selectedContactPropertyDetails}
      />
      <Overlay
        showOverlay={showOverlay}
        content={
          <ContactForm
            onClose={handleCloseForm}
            onSubmit={handleSubmitContactForm}
            sendResetPasswordLinkToUser={sendResetPasswordLinkToUser}
            sendActivationLinkToUser={sendActivationLinkToUser}
            pageUsedFrom="Contacts"
          />
        }
      />
    </>
  );
};

export default ContactsTable;
